import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, MapPin, PawPrint, Cigarette, ChevronDown } from "lucide-react";
import { Section, Eyebrow } from "@/components/common/Section";
import { Reveal } from "@/components/common/Reveal";
import {
  bookingLink,
  fromPrice,
  getPropertyBySlug,
  money,
  statusLabel,
} from "@/lib/properties";

const PINE = "#14231B";
const STONE = "#F8F5EF";
const LARCH = "#E3B268";
const INK = "#201E1A";

/* Hard-coded fallback subtitles keyed by unit name — used until the
 * `subtitle` column exists in the unit_types table. Admin edits will
 * override these once the column is present. */
const SUBTITLE_FALLBACK = {
  "The Upper Three": "3-Bed Upper · Floors 2–3",
  "The Lower Two": "2-Bed Lower · Main + Lower Level",
};
const getSubtitle = (u) => u.subtitle || SUBTITLE_FALLBACK[u.name] || null;

export default function ResidenceDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [state, setState] = useState({ loading: true, err: null });
  const [activeUnit, setActiveUnit] = useState(0);

  useEffect(() => {
    setState({ loading: true, err: null });
    getPropertyBySlug(slug)
      .then((data) => {
        setP(data);
        setState({ loading: false, err: null });
      })
      .catch((e) => setState({ loading: false, err: e.message }));
  }, [slug]);

  // SEO
  useEffect(() => {
    if (!p) return;
    // Per-property SEO overrides — keep hardcoded until we add a `seo_title`
    // field on properties.
    const SEO = {
      "the-crawford": {
        title: "The Crawford — Brand New Mill Woods Rentals | ROAM Living",
        description:
          "Eight never-lived-in 2 & 3 bedroom stacked townhomes in Crawford Plains, Mill Woods. In-suite laundry, optional garage, minutes to the Henday. From $1,650. Tour 7 days a week.",
      },
    };
    const seo = SEO[p.slug];
    document.title =
      seo?.title ||
      `${p.name} — ${p.community || p.city} Rentals | ROAM Living`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", seo?.description || p.tagline || "");
    const og = document.querySelector('meta[property="og:image"]');
    if (og && p.hero_images_resolved?.[0])
      og.setAttribute("content", p.hero_images_resolved[0]);
  }, [p]);

  if (state.loading) {
    return (
      <main className="grid min-h-[70vh] place-items-center" style={{ backgroundColor: STONE }}>
        <p className="opacity-60">Loading residence…</p>
      </main>
    );
  }
  if (state.err || !p) {
    return (
      <main
        className="grid min-h-[70vh] place-items-center px-6 text-center"
        style={{ backgroundColor: STONE }}
      >
        <div>
          <p className="label-caps text-[11px] opacity-60">Not found</p>
          <h1 className="font-display mt-3" style={{ fontSize: 42, color: INK }}>
            This residence isn&rsquo;t live.
          </h1>
          <Link to="/residences" className="pill pill-gold mt-8 inline-block">
            Back to all residences
          </Link>
        </div>
      </main>
    );
  }

  const price = fromPrice(p.unit_types);
  const isFullyLeased = p.status === "fully_leased";
  const primaryCtaLabel = isFullyLeased ? "Join the waitlist" : "Book a showing";

  return (
    <main data-testid="residence-detail-page" style={{ backgroundColor: STONE }}>
      {/* STICKY MINI-BAR */}
      <StickyMiniBar
        name={p.name}
        price={price}
        ctaLabel={primaryCtaLabel}
        ctaHref={bookingLink({ propertySlug: p.slug })}
      />

      {/* HERO GALLERY */}
      <HeroGallery
        images={p.hero_images_resolved}
        name={p.name}
        status={p.status}
        tagline={p.tagline}
        community={p.community}
        city={p.city}
      />

      {/* UNIT TYPES */}
      {p.unit_types.length > 0 && (
        <Section bg={STONE} py="py-24 md:py-32">
          <Reveal>
            <p
              className="label-caps text-[11px]"
              style={{ color: "rgba(32,30,26,0.55)" }}
            >
              Homes available
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <h2
              className="font-display mt-4"
              style={{ fontSize: "clamp(36px,5vw,64px)", color: INK, lineHeight: 1.02 }}
            >
              {p.unit_types.length === 1
                ? "One home, thoughtfully specified."
                : "Two floor plans, two ways to live here."}
            </h2>
          </Reveal>
          {p.slug === "the-crawford" && p.unit_types.length === 2 && (
            <Reveal delay={0.1}>
              <p
                className="body-measure mt-4 text-[16px]"
                style={{ color: "rgba(32,30,26,0.65)" }}
              >
                One building, two ways to live in it — four upper three-bedrooms
                stacked over four lower two-bedrooms.
              </p>
            </Reveal>
          )}

          <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-2">
            {p.unit_types.map((u) => (
              <UnitCard
                key={u.id}
                unit={u}
                propertySlug={p.slug}
                waitlist={isFullyLeased}
              />
            ))}
          </div>
        </Section>
      )}

      {/* WALK IT WITH US */}
      {p.video_url && <VideoSection url={p.video_url} name={p.name} />}

      {/* WHAT EVERY HOME INCLUDES — editorial */}
      <Includes p={p} />

      {/* FLOOR PLAN TABS */}
      {p.unit_types.some((u) => u.floorplan_resolved) && (
        <FloorPlanTabs
          units={p.unit_types}
          active={activeUnit}
          setActive={setActiveUnit}
        />
      )}

      {/* NEIGHBOURHOOD */}
      {(p.neighbourhood_blurb || (p.lat && p.lng)) && (
        <Neighbourhood
          blurb={p.neighbourhood_blurb}
          community={p.community}
          city={p.city}
          lat={p.lat}
          lng={p.lng}
        />
      )}

      {/* FAQ */}
      {Array.isArray(p.faq) && p.faq.length > 0 && <FaqAccordion items={p.faq} />}

      {/* MOBILE CTA BAR */}
      <div className="md:hidden" style={{ height: 88 }} />
      <div
        className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-between gap-3 border-t px-4 py-3 md:hidden"
        style={{
          backgroundColor: "rgba(248,245,239,0.94)",
          backdropFilter: "blur(10px)",
          borderColor: "rgba(32,30,26,0.1)",
        }}
      >
        <div>
          <p
            className="label-caps text-[9px]"
            style={{ color: "rgba(32,30,26,0.55)" }}
          >
            {p.name}
          </p>
          {price && (
            <p className="text-[13px]" style={{ color: INK }}>
              From {money(price)}/mo
            </p>
          )}
        </div>
        <Link
          to={bookingLink({ propertySlug: p.slug })}
          className="pill pill-gold text-[13px]"
        >
          {primaryCtaLabel}
        </Link>
      </div>
    </main>
  );
}

/* ---- HERO GALLERY ---- */
const HeroGallery = ({ images, name, status, tagline, community, city }) => {
  const [i, setI] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const cover = images?.[i];
  return (
    <section
      className="relative overflow-hidden"
      style={{ backgroundColor: PINE, paddingTop: 96, paddingBottom: 40 }}
      data-nav-theme="dark"
      data-testid="residence-hero"
    >
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <Link
          to="/residences"
          className="label-caps inline-flex items-center gap-2 text-[10px]"
          style={{ color: "rgba(237,233,224,0.6)" }}
        >
          <ArrowLeft size={12} /> Back to residences
        </Link>
        <div className="mt-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <span
              className="label-caps rounded-full px-3 py-1 text-[10px]"
              style={{
                backgroundColor: LARCH,
                color: PINE,
                letterSpacing: "0.24em",
              }}
              data-testid="residence-status-pill"
            >
              {statusLabel(status).toUpperCase()}
              {status === "now_leasing" ? " · BRAND NEW" : ""}
            </span>
            <h1
              className="font-display mt-6"
              style={{
                fontSize: "clamp(52px,8vw,104px)",
                color: STONE,
                lineHeight: 0.95,
                letterSpacing: "-0.015em",
              }}
              data-testid="residence-name"
            >
              {name}
            </h1>
            {(community || city) && (
              <p
                className="mt-3 text-[15px]"
                style={{ color: "rgba(237,233,224,0.7)" }}
              >
                {community}
                {city ? ` · ${city}` : ""}
              </p>
            )}
            {tagline && (
              <p
                className="body-measure mt-5 max-w-[46ch] text-[17px]"
                style={{ color: "rgba(237,233,224,0.86)" }}
              >
                {tagline}
              </p>
            )}
          </div>
        </div>

        {images?.length > 0 && (
          <>
            <div
              className="relative mt-10 overflow-hidden"
              style={{ borderRadius: 24, aspectRatio: "16 / 9", cursor: "zoom-in" }}
              onClick={() => setLightbox(true)}
              data-testid="residence-hero-image"
            >
              <AnimatePresence mode="wait">
                <motion.img
                  key={i}
                  src={cover}
                  alt={`${name} — photo ${i + 1}`}
                  className="absolute inset-0 h-full w-full object-cover"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                />
              </AnimatePresence>
            </div>
            {images.length > 1 && (
              <div className="mt-4 flex gap-3 overflow-x-auto pb-2">
                {images.map((src, idx) => (
                  <button
                    key={idx}
                    onClick={() => setI(idx)}
                    className="shrink-0 overflow-hidden"
                    style={{
                      width: 120,
                      height: 76,
                      borderRadius: 10,
                      border: idx === i ? `2px solid ${LARCH}` : "2px solid transparent",
                      opacity: idx === i ? 1 : 0.7,
                    }}
                    data-testid={`residence-thumb-${idx}`}
                  >
                    <img src={src} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="fixed inset-0 z-[100] grid place-items-center p-6"
            style={{ backgroundColor: "rgba(20,35,27,0.95)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(false)}
          >
            <img
              src={cover}
              alt=""
              className="max-h-[90vh] max-w-[92vw] rounded-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={() => setLightbox(false)}
              className="label-caps absolute right-6 top-6 text-[11px]"
              style={{ color: STONE }}
            >
              Close ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

/* ---- STICKY MINI-BAR ---- */
const StickyMiniBar = ({ name, price, ctaLabel, ctaHref }) => {
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const on = () => setShown(window.scrollY > 700);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <AnimatePresence>
      {shown && (
        <motion.div
          initial={{ y: -60, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -60, opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="fixed inset-x-0 top-0 z-40 hidden items-center justify-between gap-6 px-8 py-3 md:flex"
          style={{
            backgroundColor: "rgba(248,245,239,0.94)",
            backdropFilter: "blur(10px)",
            borderBottom: "1px solid rgba(32,30,26,0.1)",
          }}
        >
          <div className="flex items-baseline gap-4">
            <span
              className="font-display"
              style={{ fontSize: 20, color: INK, lineHeight: 1 }}
            >
              {name}
            </span>
            {price && (
              <span className="text-[13px]" style={{ color: "#8A6220" }}>
                From {money(price)}/mo
              </span>
            )}
          </div>
          <Link to={ctaHref} className="pill pill-gold text-[13px]">
            {ctaLabel}
          </Link>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ---- UNIT CARD ---- */
const UnitCard = ({ unit, propertySlug, waitlist }) => {
  const avail = unit.units_available ?? 0;
  const total = unit.units_total ?? 0;
  const ctaLabel = waitlist || avail === 0 ? "Join the waitlist" : "Book this floor plan";
  const href = bookingLink({
    propertySlug,
    unitTypeName: unit.name,
    source: "website",
  });
  const hero = unit.photos_resolved?.[0];
  return (
    <motion.article
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6 }}
      className="overflow-hidden"
      style={{ backgroundColor: "#FFFFFF", borderRadius: 24 }}
      data-testid={`unit-card-${unit.id}`}
    >
      {hero && (
        <div style={{ aspectRatio: "5 / 3", overflow: "hidden" }}>
          <img src={hero} alt="" className="h-full w-full object-cover" />
        </div>
      )}
      <div className="p-6 md:p-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            {getSubtitle(unit) && (
              <p
                className="label-caps text-[10px]"
                style={{ color: "#8A6220", letterSpacing: "0.22em" }}
                data-testid={`unit-subtitle-${unit.id}`}
              >
                {getSubtitle(unit)}
              </p>
            )}
            <h3
              className="font-display mt-2"
              style={{ fontSize: 30, color: INK, lineHeight: 1.05 }}
            >
              {unit.name}
            </h3>
            <p
              className="mt-2 text-[13px]"
              style={{ color: "rgba(32,30,26,0.6)" }}
            >
              {unit.beds} bed · {unit.baths} bath · {unit.sqft} sq ft
            </p>
          </div>
          <p
            className="whitespace-nowrap font-display"
            style={{ color: "#8A6220", fontSize: 22 }}
          >
            {unit.rent_from === unit.rent_to
              ? money(unit.rent_from)
              : `${money(unit.rent_from)}–${money(unit.rent_to)}`}
            <span
              className="ml-1 text-[12px] font-normal"
              style={{ color: "rgba(32,30,26,0.5)" }}
            >
              /mo
            </span>
          </p>
        </div>
        {unit.description && (
          <p
            className="mt-4 text-[15px] leading-[1.55]"
            style={{ color: "rgba(32,30,26,0.75)" }}
          >
            {unit.description}
          </p>
        )}
        {unit.spec_chips?.length > 0 && (
          <ul className="mt-5 flex flex-wrap gap-2">
            {unit.spec_chips.map((chip) => (
              <li
                key={chip}
                className="label-caps rounded-full px-3 py-1 text-[10px]"
                style={{
                  backgroundColor: "rgba(200,154,78,0.14)",
                  color: "#8A6220",
                  border: "1px solid rgba(200,154,78,0.35)",
                }}
              >
                {chip}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-6 flex items-center justify-between">
          <p
            className="label-caps text-[10px]"
            style={{ color: "rgba(32,30,26,0.55)" }}
          >
            {avail} of {total} available
          </p>
          <Link to={href} className="pill pill-gold text-[13px]">
            {ctaLabel}
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

/* ---- VIDEO ---- */
const VideoSection = ({ url, name }) => (
  <Section bg={PINE} py="py-24 md:py-32">
    <div className="mx-auto max-w-[1440px]" data-nav-theme="dark">
      <Reveal>
        <p className="label-caps text-[11px]" style={{ color: "rgba(174,196,199,0.7)" }}>
          Walk it with us
        </p>
      </Reveal>
      <Reveal delay={0.05}>
        <h2
          className="font-display mt-4 max-w-[16ch]"
          style={{ fontSize: "clamp(36px,5vw,64px)", color: STONE, lineHeight: 1.02 }}
        >
          A film of the home.
        </h2>
      </Reveal>
      <div
        className="mt-10 overflow-hidden"
        style={{ borderRadius: 24, aspectRatio: "16/9" }}
      >
        <iframe
          src={url}
          title={`${name} film`}
          className="h-full w-full"
          allow="autoplay; encrypted-media; picture-in-picture"
        />
      </div>
    </div>
  </Section>
);

/* ---- INCLUDES ---- */
const Includes = ({ p }) => (
  <Section bg={STONE} py="py-24 md:py-32">
    <Reveal>
      <p className="label-caps text-[11px]" style={{ color: "rgba(32,30,26,0.55)" }}>
        Standard on every home
      </p>
    </Reveal>
    <Reveal delay={0.05}>
      <h2
        className="font-display mt-4 max-w-[18ch]"
        style={{ fontSize: "clamp(32px,4.4vw,54px)", color: INK, lineHeight: 1.02 }}
      >
        Included — no upsell.
      </h2>
    </Reveal>
    <ul className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-3">
      {[
        p.pet_policy && { icon: PawPrint, label: "Pet policy", body: p.pet_policy },
        p.smoking_policy && {
          icon: Cigarette,
          label: "Smoking",
          body: p.smoking_policy,
        },
        p.available_from && {
          icon: MapPin,
          label: "Available",
          body: new Date(p.available_from).toLocaleDateString("en-CA", {
            month: "long",
            day: "numeric",
            year: "numeric",
          }),
        },
      ]
        .filter(Boolean)
        .map((row, i) => (
          <Reveal key={i} delay={i * 0.05}>
            <div
              className="flex items-start gap-4 p-6"
              style={{
                border: "1px solid rgba(32,30,26,0.1)",
                borderRadius: 20,
                backgroundColor: "#FFFFFF",
              }}
            >
              <span
                className="grid h-9 w-9 shrink-0 place-items-center rounded-full"
                style={{ backgroundColor: PINE }}
              >
                <row.icon size={16} color={LARCH} strokeWidth={1.8} />
              </span>
              <div>
                <p
                  className="label-caps text-[10px]"
                  style={{ color: "rgba(32,30,26,0.55)" }}
                >
                  {row.label}
                </p>
                <p className="mt-1 text-[15px]" style={{ color: INK }}>
                  {row.body}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
    </ul>
  </Section>
);

/* ---- FLOOR PLANS — new layout ---- */
const FloorPlanTabs = ({ units, active, setActive }) => {
  const withPlans = units.filter((u) => (u.floorplan_plans || []).length > 0);
  const [floor, setFloor] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  if (withPlans.length === 0) return null;
  const cur = withPlans[Math.min(active, withPlans.length - 1)];
  const plans = cur.floorplan_plans || [];
  const plan = plans[Math.min(floor, plans.length - 1)];

  // reset floor to 0 whenever the unit type changes
  const switchUnit = (i) => {
    setActive(i);
    setFloor(0);
  };

  return (
    <section style={{ backgroundColor: "#EDE9E0" }} className="py-24 md:py-32">
      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <Reveal>
          <p className="label-caps text-[11px]" style={{ color: "rgba(32,30,26,0.55)" }}>
            Floor plans
          </p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2
            className="font-display mt-3 max-w-[18ch]"
            style={{ fontSize: "clamp(32px,4.4vw,54px)", color: INK, lineHeight: 1.02 }}
          >
            See how the home lives.
          </h2>
        </Reveal>

        {/* Unit-type toggle — sits above the viewer, left-aligned */}
        <div className="mt-8 flex flex-wrap gap-2">
          {withPlans.map((u, i) => (
            <button
              key={u.id}
              onClick={() => switchUnit(i)}
              className="label-caps rounded-full px-4 py-2 text-[10px] transition-colors"
              style={{
                backgroundColor: i === active ? PINE : "transparent",
                color: i === active ? STONE : INK,
                border:
                  i === active
                    ? "1px solid transparent"
                    : "1px solid rgba(32,30,26,0.2)",
              }}
              data-testid={`floorplan-tab-${i}`}
            >
              {u.name}
            </button>
          ))}
        </div>

        <div
          className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-[60%_40%] md:gap-8"
          data-testid="floorplan-stage"
        >
          {/* PLAN VIEWER — paper card, plan tabs inside */}
          <AnimatePresence mode="wait">
            <motion.div
              key={cur.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="relative flex flex-col"
              style={{
                backgroundColor: STONE,
                borderRadius: 24,
                padding: 24,
                border: "1px solid rgba(32,30,26,0.08)",
                boxShadow: "0 30px 60px -30px rgba(20,35,27,0.28)",
              }}
            >
              {/* Floor tabs (inside the card, top-left) */}
              {plans.length > 1 && (
                <div
                  className="mb-4 flex flex-wrap gap-1.5"
                  data-testid="floorplan-floor-tabs"
                >
                  {plans.map((p, i) => (
                    <button
                      key={i}
                      onClick={() => setFloor(i)}
                      className="label-caps rounded-full px-3 py-1.5 text-[9.5px] transition-colors"
                      style={{
                        backgroundColor: i === floor ? PINE : "transparent",
                        color: i === floor ? STONE : INK,
                        border:
                          i === floor
                            ? "1px solid transparent"
                            : "1px solid rgba(32,30,26,0.18)",
                      }}
                      data-testid={`floorplan-floor-${i}`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              )}

              {/* The plan itself — swipe-left/right on touch */}
              <div
                className="relative flex-1"
                onTouchStart={(e) => (e.currentTarget._sx = e.touches[0].clientX)}
                onTouchEnd={(e) => {
                  const sx = e.currentTarget._sx;
                  if (sx == null) return;
                  const dx = e.changedTouches[0].clientX - sx;
                  if (Math.abs(dx) > 50 && plans.length > 1) {
                    setFloor((f) => (f + (dx < 0 ? 1 : -1) + plans.length) % plans.length);
                  }
                }}
              >
                <AnimatePresence mode="wait">
                  <motion.button
                    key={cur.id + "-" + floor}
                    type="button"
                    onClick={() => setLightbox(true)}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    aria-label={`Open ${cur.name} — ${plan.label} at full size`}
                    className="group block h-full w-full cursor-zoom-in"
                    data-testid="floorplan-image-btn"
                  >
                    <img
                      src={plan.image_resolved}
                      alt={`${cur.name} — ${plan.label}`}
                      className="mx-auto block max-h-[520px] w-full object-contain transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </motion.button>
                </AnimatePresence>

                {/* Dot indicators (mobile especially) */}
                {plans.length > 1 && (
                  <div className="mt-3 flex justify-center gap-1.5 md:hidden">
                    {plans.map((_, i) => (
                      <span
                        key={i}
                        aria-current={i === floor}
                        className="block h-1.5 rounded-full transition-all duration-300"
                        style={{
                          width: i === floor ? 18 : 6,
                          backgroundColor:
                            i === floor ? "#8A6220" : "rgba(32,30,26,0.2)",
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>

              {/* Caption line under the plan */}
              {plan.caption && (
                <AnimatePresence mode="wait">
                  <motion.p
                    key={cur.id + "-cap-" + floor}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={{ duration: 0.3 }}
                    className="mt-6 border-t pt-4 text-[13.5px] italic leading-[1.55]"
                    style={{
                      color: "rgba(32,30,26,0.62)",
                      borderColor: "rgba(32,30,26,0.1)",
                    }}
                    data-testid="floorplan-caption"
                  >
                    {plan.caption}
                  </motion.p>
                </AnimatePresence>
              )}
            </motion.div>
          </AnimatePresence>

          {/* META RAIL — right column */}
          <AnimatePresence mode="wait">
            <motion.aside
              key={cur.id + "-meta"}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-col"
              style={{
                backgroundColor: STONE,
                borderRadius: 24,
                padding: 24,
                border: "1px solid rgba(32,30,26,0.08)",
              }}
            >
              <p
                className="label-caps text-[10px]"
                style={{ color: "#8A6220", letterSpacing: "0.22em" }}
              >
                {SUBTITLE_FALLBACK[cur.name] || cur.subtitle || "Floor plan"}
              </p>
              <h3
                className="font-display mt-2"
                style={{ fontSize: 28, color: INK, lineHeight: 1.1 }}
              >
                {cur.name}
              </h3>
              <ul
                className="mt-5 grid grid-cols-3 gap-2 border-t pt-5"
                style={{ borderColor: "rgba(32,30,26,0.1)" }}
              >
                {[
                  ["Bed", cur.beds],
                  ["Bath", cur.baths],
                  ["Sqft", cur.sqft],
                ].map(([l, v]) => (
                  <li key={l}>
                    <p
                      className="font-display"
                      style={{ fontSize: 22, color: INK, lineHeight: 1 }}
                    >
                      {v ?? "—"}
                    </p>
                    <p
                      className="label-caps mt-1 text-[9px]"
                      style={{ color: "rgba(32,30,26,0.55)" }}
                    >
                      {l}
                    </p>
                  </li>
                ))}
              </ul>
              <p
                className="mt-5 border-t pt-4 font-display"
                style={{
                  fontSize: 22,
                  color: "#8A6220",
                  borderColor: "rgba(32,30,26,0.1)",
                }}
              >
                {cur.rent_from === cur.rent_to
                  ? money(cur.rent_from)
                  : `${money(cur.rent_from)}–${money(cur.rent_to)}`}
                <span
                  className="ml-1 text-[12px] font-normal"
                  style={{ color: "rgba(32,30,26,0.5)" }}
                >
                  /mo
                </span>
              </p>
              <p
                className="label-caps mt-2 text-[10px]"
                style={{ color: "rgba(32,30,26,0.55)" }}
              >
                {cur.units_available} of {cur.units_total} available
              </p>
              {cur.spec_chips?.length > 0 && (
                <ul className="mt-5 flex flex-wrap gap-1.5">
                  {cur.spec_chips.slice(0, 5).map((chip) => (
                    <li
                      key={chip}
                      className="label-caps rounded-full px-2 py-0.5 text-[9px]"
                      style={{
                        backgroundColor: "rgba(200,154,78,0.14)",
                        color: "#8A6220",
                      }}
                    >
                      {chip}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-auto pt-6">
                <button
                  onClick={() => setLightbox(true)}
                  className="pill w-full text-[13px]"
                  style={{
                    border: "1px solid rgba(32,30,26,0.2)",
                    color: INK,
                    backgroundColor: "transparent",
                  }}
                >
                  View full plan
                </button>
              </div>
            </motion.aside>
          </AnimatePresence>
        </div>
      </div>

      {/* LIGHTBOX */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="fixed inset-0 z-[110] flex items-center justify-center p-6"
            style={{ backgroundColor: "rgba(20,35,27,0.96)" }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(false)}
          >
            <img
              src={plan.image_resolved}
              alt={`${cur.name} — ${plan.label}`}
              className="max-h-[92vh] max-w-[94vw] object-contain"
              onClick={(e) => e.stopPropagation()}
              style={{ borderRadius: 16 }}
            />
            <button
              onClick={() => setLightbox(false)}
              className="label-caps absolute right-6 top-6 text-[11px]"
              style={{ color: STONE }}
            >
              Close ✕
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

/* ---- NEIGHBOURHOOD ---- */
const Neighbourhood = ({ blurb, community, city, lat, lng }) => (
  <Section bg={STONE} py="py-24 md:py-32">
    <div className="grid grid-cols-1 gap-10 md:grid-cols-2">
      <div>
        <Reveal>
          <p className="label-caps text-[11px]" style={{ color: "rgba(32,30,26,0.55)" }}>
            The neighbourhood
          </p>
        </Reveal>
        <Reveal delay={0.05}>
          <h2
            className="font-display mt-4 max-w-[16ch]"
            style={{ fontSize: "clamp(32px,4.4vw,54px)", color: INK, lineHeight: 1.02 }}
          >
            {community || "The community"}.
          </h2>
        </Reveal>
        {blurb && (
          <Reveal delay={0.1}>
            <p
              className="body-measure mt-6 text-[16px]"
              style={{ color: "rgba(32,30,26,0.75)" }}
            >
              {blurb}
            </p>
          </Reveal>
        )}
      </div>
      <div>
        {lat && lng ? (
          <MapPreview lat={lat} lng={lng} />
        ) : (
          <div
            className="grid place-items-center text-[14px]"
            style={{
              borderRadius: 24,
              aspectRatio: "4/3",
              backgroundColor: "#EDE9E0",
              color: "rgba(32,30,26,0.5)",
            }}
          >
            Map coming soon.
          </div>
        )}
      </div>
    </div>
  </Section>
);

const MapPreview = ({ lat, lng }) => {
  // Static Mapbox preview with a gold marker.
  //
  // IMPORTANT: the interactive map uses REACT_APP_MAPBOX_STYLE (custom
  // "Monochrome" style). That style is Standard-based / v3 — the Static
  // Images API can't render it and returns a black canvas with only the
  // pin visible. So this preview intentionally uses `mapbox/light-v11`,
  // which is rendered server-side and gives a warm, subtle basemap that
  // fits the palette. Do NOT swap this to a custom style unless the
  // Static API has been verified to support it.
  const token = process.env.REACT_APP_MAPBOX_TOKEN;
  const style = "mapbox/light-v11";
  if (!token) return null;
  const url = `https://api.mapbox.com/styles/v1/${style}/static/pin-l+e3b268(${lng},${lat})/${lng},${lat},14/800x600@2x?access_token=${token}`;
  return (
    <img
      src={url}
      alt="Location map"
      className="w-full object-cover"
      style={{
        borderRadius: 24,
        aspectRatio: "4/3",
        border: "1px solid rgba(32,30,26,0.08)",
        boxShadow: "0 20px 40px -20px rgba(20,35,27,0.25)",
      }}
    />
  );
};

/* ---- FAQ ---- */
const FaqAccordion = ({ items }) => {
  const [open, setOpen] = useState(0);
  return (
    <Section bg="#EDE9E0" py="py-24 md:py-32">
      <Reveal>
        <p className="label-caps text-[11px]" style={{ color: "rgba(32,30,26,0.55)" }}>
          Questions
        </p>
      </Reveal>
      <Reveal delay={0.05}>
        <h2
          className="font-display mt-4"
          style={{ fontSize: "clamp(32px,4.4vw,54px)", color: INK, lineHeight: 1.02 }}
        >
          Good to know.
        </h2>
      </Reveal>
      <div className="mt-10 divide-y" style={{ borderColor: "rgba(32,30,26,0.1)" }}>
        {items.map((f, i) => {
          const isOpen = open === i;
          return (
            <div
              key={i}
              className="border-t"
              style={{ borderColor: "rgba(32,30,26,0.1)" }}
              data-testid={`faq-row-${i}`}
            >
              <button
                onClick={() => setOpen(isOpen ? -1 : i)}
                className="flex w-full items-center justify-between py-6 text-left"
              >
                <span
                  className="font-display"
                  style={{ fontSize: 22, color: INK, lineHeight: 1.2 }}
                >
                  {f.question}
                </span>
                <ChevronDown
                  size={20}
                  style={{
                    color: "rgba(32,30,26,0.6)",
                    transform: isOpen ? "rotate(180deg)" : "none",
                    transition: "transform 0.25s ease",
                  }}
                />
              </button>
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    style={{ overflow: "hidden" }}
                  >
                    <p
                      className="pb-6 pr-10 text-[15px] leading-[1.6]"
                      style={{ color: "rgba(32,30,26,0.75)" }}
                    >
                      {f.answer}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </Section>
  );
};
